import { Component, OnInit, Renderer2, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AWSCognitoErrorResponse } from '../models/error.model';

@Component({
  selector: 'online-app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {

  constructor(private router: Router,
    private fb: FormBuilder,
    private authService: AuthService,
    private renderer: Renderer2,
    private store: Store<any>) {
    this.renderer.addClass(document.body, 'login-screen');
  }

  sharedStateSubscription: Subscription;
  forgotPasswordDetails: any;
  resetPasswordFormGroup: FormGroup;
  isFormSubmitted = false;
  loginIdErrorMessage = '';
  tooltipArray: string[] = [];
  tooltipArrayDisplay: string;

  ngOnInit() {

    this.resetPasswordFormGroup = this.fb.group({
      loginId: ['', Validators.required],
      accountPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6)]],
    });


    this.setTooltip();

    this.sharedStateSubscription = this.store
      .pipe(select('shared'))
      .subscribe(sharedState => {
        if (sharedState) {
          this.forgotPasswordDetails = sharedState.forgotPasswordDetails;
        } else {
          this.router.navigate(['/forgot-password']);
        }
      });

  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, 'login-screen');
  }

  validatePasswords(): boolean {
    const accountPassword = this.resetPasswordFormGroup.get('accountPassword').value;
    const confirmPassword = this.resetPasswordFormGroup.get('confirmPassword').value;

    return accountPassword === confirmPassword;

  }

  validateLoginId(loginId): boolean {
    const formLoginId = this.resetPasswordFormGroup.get('loginId').value;
    return formLoginId.toLowerCase() === loginId.toLowerCase();
  }

  resetPassword() {

    this.isFormSubmitted = true;


    if (this.resetPasswordFormGroup.valid) {

      if (!this.validatePasswords()) {
        this.resetPasswordFormGroup.get('accountPassword').setErrors({ passwordsDontMatch: true });
        this.resetPasswordFormGroup.updateValueAndValidity();
        return;
      }

      const { username, verificationCode } = this.forgotPasswordDetails;

      if (!this.validateLoginId(username)) {
        this.resetPasswordFormGroup.get('loginId').setErrors({ invalidLoginId: true });
        this.resetPasswordFormGroup.updateValueAndValidity();
        return;
      }

      const password = this.resetPasswordFormGroup.get('accountPassword').value;
      this.authService.resetPassword(username, verificationCode, password)
        .subscribe(response => {
          this.router.navigate(['/login']);
        }, (err: AWSCognitoErrorResponse) => {
          this.loginIdErrorMessage = err.message;
          this.resetPasswordFormGroup.get('loginId').setErrors({ loginIdApiError: true });
        });


    } else {
      
    }


  }


  setTooltip() {
    this.tooltipArray.push("1. Your password length must be between 8 and 20 characters.");
    this.tooltipArray.push("2. Your password must have at least one number.");
    this.tooltipArray.push("3. Your password must have at least one alphabet.");
    this.tooltipArray.push("4. Your password must have at least one upper case character.");
    this.tooltipArray.push("5. Your password must have at least one lower case character.");
    this.tooltipArray.push("6. Your password must contain at least one special character from the list given below:");
    this.tooltipArray.push("    ^ $ * . [ ] {} ( ) ? - \"! @ # % & / , > < ' : ; | _ ~ `");


    this.tooltipArrayDisplay = this.tooltipArray.join("\r\n");
  }

  //accessibility -- link error msg to form field
  errorStudentNumberAccessibility(): string {
    if (this.isFormSubmitted &&
      this.resetPasswordFormGroup.get('loginId').errors &&
      this.resetPasswordFormGroup.get('loginId').errors.required) {
      return "requiredStudentNumber";
    } else if (this.isFormSubmitted &&
      this.resetPasswordFormGroup.get('loginId').errors &&
      this.resetPasswordFormGroup.get('loginId').errors.invalidLoginId) {
      return "invalidStudentNumber";
    } else if (this.isFormSubmitted && this.resetPasswordFormGroup.get('loginId').errors &&
      this.resetPasswordFormGroup.get('loginId').errors.loginIdApiError) {
      return "errorStudentNumber";
    } else {
      return "";
    }
  }

  errorPasswordAccessibility(): string {
    if (this.isFormSubmitted &&
      this.resetPasswordFormGroup.get('accountPassword').errors &&
      this.resetPasswordFormGroup.get('accountPassword').errors.required) {
      return "requiredPassword";
    } else if (this.isFormSubmitted &&
      this.resetPasswordFormGroup.get('accountPassword').errors &&
      this.resetPasswordFormGroup.get('accountPassword').errors.minlength) {
      return "minlengthPassword";
    } else if (this.isFormSubmitted &&
      this.resetPasswordFormGroup.get('accountPassword').errors &&
      this.resetPasswordFormGroup.get('accountPassword').errors.passwordsDontMatch) {
      return "matchPassword";
    } else {
      return "";
    }
  }

  errorConfirmPasswordAccessibility(): string {
    if (this.isFormSubmitted &&
      this.resetPasswordFormGroup.get('confirmPassword').errors &&
      this.resetPasswordFormGroup.get('confirmPassword').errors.required) {
      return "reEnterConfirmPassword";
    } else if (this.isFormSubmitted &&
      this.resetPasswordFormGroup.get('confirmPassword').errors &&
      this.resetPasswordFormGroup.get('confirmPassword').errors.minlength) {
      return "minlengthConfirmPassword";
    } else {
      return "";
    }
  }
}
